import { useRef } from 'react';

/** What a key in the editor asks for, after its text is committed (the list decides what it means: Task 5 of P16-5). */
export type TaskEditMove = 'none' | 'newBelow' | 'up' | 'down' | 'indent' | 'outdent' | 'moveUp' | 'moveDown' | 'toggle' | 'deleteEmpty';

/**
 * One-line editor for a task or a list's title: Enter, arrows, Tab and their modified forms commit and move on, blur
 * commits, Escape cancels. No key reaches the canvas while it is open.
 */
export function TaskEditor({
  value,
  maxLength,
  label,
  className = '',
  onCommit,
  onCancel,
}: {
  value: string;
  maxLength: number;
  label: string;
  className?: string;
  onCommit: (text: string, next: TaskEditMove) => void;
  onCancel: () => void;
}) {
  const done = useRef(false);

  const finish = (text: string, next: TaskEditMove) => {
    if (done.current) return;
    done.current = true;
    onCommit(text, next);
  };
  return (
    <input
      type="text"
      autoFocus
      data-canvas-ignore=""
      data-task-editor=""
      aria-label={label}
      defaultValue={value}
      maxLength={maxLength}
      onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
      onBlur={(e) => finish(e.currentTarget.value, 'none')}
      onKeyDown={(e) => {
        e.stopPropagation();
        const text = e.currentTarget.value;
        const move = (next: TaskEditMove) => {
          e.preventDefault();
          finish(text, next);
        };
        if (e.key === 'Escape') {
          e.preventDefault();
          done.current = true;
          onCancel();
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) move('toggle');
        else if (e.key === 'Enter' && !e.shiftKey) move('newBelow');
        else if (e.key === 'Tab') move(e.shiftKey ? 'outdent' : 'indent');
        else if (e.key === 'ArrowUp') move(e.altKey ? 'moveUp' : 'up');
        else if (e.key === 'ArrowDown') move(e.altKey ? 'moveDown' : 'down');
        else if (e.key === 'Backspace' && text === '') move('deleteEmpty');
      }}
      className={`min-w-0 flex-1 rounded-[4px] bg-white px-1 text-[#141414] outline-2 outline-[#3b82f6] ${className}`}
    />
  );
}
